import { forwardRef, memo, useCallback, useMemo } from 'react';
import { Box, Flex } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconChevronRight } from '@tabler/icons-react';

import { Text } from '~/domains/design-system/Text';
import { Routes } from '~/domains/common/constants/routes';
import { PrefetchLink } from '~/components/shared/PrefetchLink/PrefetchLink';
import { createMarketDetailPrefetchConfig } from '~/domains/market/utils/prefetch';
import { useMarketMatchupLabel } from '~/domains/market/state/selectors/marketCard.selectors';

import { useStyles } from './MarketCard.styles';
import type { MarketCardProps, MarketType, TeamType } from './types';
import { MarketTeamRow } from './MarketTeamRow';
import { MarketCardGameStatus } from './MarketCardGameStatus';

type MarketClickHandler = (
  e: React.MouseEvent,
  market: MarketType,
  team: TeamType,
  marketId: string
) => void;

type MarketGroup = {
  marketGroupId: string;
};

type HeaderProps = MarketGroup;
type TeamsProps = MarketGroup & {
  onMarketClick: MarketClickHandler;
};

const MARKET_HEADER_VARIANT = { base: 'subhead-x-small', md: 'subhead-small' } as const;

const Header = memo(({ marketGroupId }: HeaderProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation('market');

  return (
    <div className={classes.header}>
      <MarketCardGameStatus marketGroupId={marketGroupId} />
      <div className={classes.marketHeaders}>
        <Text variant={MARKET_HEADER_VARIANT} c="gray2.3" className={classes.marketHeaderCell}>
          {t('card.winBy')}
        </Text>
        <Text variant={MARKET_HEADER_VARIANT} c="gray2.3" className={classes.marketHeaderCell}>
          {t('card.total')}
        </Text>
        <Text variant={MARKET_HEADER_VARIANT} c="gray2.3" className={classes.marketHeaderCell}>
          {t('card.winner')}
        </Text>
      </div>
    </div>
  );
});

Header.displayName = 'Header';

const Teams = memo(({ marketGroupId, onMarketClick }: TeamsProps) => {
  const { classes } = useStyles();

  return (
    <div className={classes.teamsContainer}>
      <MarketTeamRow marketGroupId={marketGroupId} teamType="away" onMarketClick={onMarketClick} />
      <MarketTeamRow marketGroupId={marketGroupId} teamType="home" onMarketClick={onMarketClick} />
    </div>
  );
});

Teams.displayName = 'Teams';

const Footer = memo(() => {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('market');

  return (
    <Flex
      align="center"
      mt={theme.other.spacing._16}
      justify="center"
      gap={theme.other.spacing._4}
      className={classes.gameDetails}
    >
      <Text variant="subhead-medium" c="gray2.3">
        {t('card.gameDetails')}
      </Text>
      <IconChevronRight size={16} color={theme.colors.gray2[3]} />
    </Flex>
  );
});

Footer.displayName = 'Footer';

const Full = memo(
  forwardRef<HTMLDivElement, MarketCardProps>(
    // eslint-disable-next-line react/prop-types
    ({ marketGroupId, onClick, onMarketClick, className, ...props }, ref) => {
      const { classes, cx } = useStyles();
      const matchupLabel = useMarketMatchupLabel(marketGroupId);
      const prefetchConfig = useMemo(
        () => createMarketDetailPrefetchConfig(marketGroupId),
        [marketGroupId]
      );

      const handleCardClick = useCallback(
        (e: React.MouseEvent) => {
          if ((e.target as HTMLElement).closest('[data-market-cell]')) {
            e.preventDefault();
            return;
          }
          onClick?.();
        },
        [onClick]
      );

      const handleMarketClick: MarketClickHandler = useCallback(
        (e, market, team, marketId) => {
          e.stopPropagation();
          onMarketClick?.(market, team, marketId);
        },
        [onMarketClick]
      );

      return (
        <PrefetchLink
          href={Routes.marketsEvent(marketGroupId)}
          prefetchConfig={prefetchConfig}
          className={classes.cardLink}
          {...(matchupLabel ? { 'aria-label': matchupLabel } : {})}
        >
          <Box
            ref={ref}
            className={cx(classes.container, !onClick && classes.notClickable, className)}
            onClick={handleCardClick}
            {...props}
          >
            <Header marketGroupId={marketGroupId} />
            <Teams marketGroupId={marketGroupId} onMarketClick={handleMarketClick} />
            <Footer />
          </Box>
        </PrefetchLink>
      );
    }
  )
);

Full.displayName = 'MarketCard.Full';

const Compact = memo(
  forwardRef<HTMLDivElement, MarketCardProps>(
    // eslint-disable-next-line react/prop-types
    ({ marketGroupId, onClick, onMarketClick, className, ...props }, ref) => {
      const { classes, cx } = useStyles();

      const handleCardClick = useCallback(
        (e: React.MouseEvent) => {
          if ((e.target as HTMLElement).closest('[data-market-cell]')) {
            e.preventDefault();
            return;
          }
          onClick?.();
        },
        [onClick]
      );

      const handleMarketClick: MarketClickHandler = useCallback(
        (e, market, team, marketId) => {
          e.stopPropagation();
          onMarketClick?.(market, team, marketId);
        },
        [onMarketClick]
      );

      return (
        <Box
          ref={ref}
          className={cx(classes.container, !onClick && classes.notClickable, className)}
          onClick={handleCardClick}
          {...props}
        >
          <Teams marketGroupId={marketGroupId} onMarketClick={handleMarketClick} />
        </Box>
      );
    }
  )
);

Compact.displayName = 'MarketCard.Compact';

export const MarketCard = Object.assign(Full, {
  Full,
  Compact,
  Header,
  Teams,
  Footer,
});
